import { useMemo } from 'react';
import { useAppSelector } from '../../../hooks/reduxHooks';

export interface HolidayPermissions {
  canView: boolean;
  canRequest: boolean;
  canManage: boolean;
  isAdmin: boolean;
}

/**
 * Hook to check holiday-related permissions based on user role
 * NOTE: By default, all authenticated users can view and request holidays
 * Admin functions (manage) are restricted to admins only
 */
export const useHolidayPermissions = (): HolidayPermissions => {
  const user = useAppSelector((state) => state.auth.user);

  return useMemo(() => {
    if (!user) {
      return {
        canView: false,
        canRequest: false,
        canManage: false,
        isAdmin: false,
      };
    }

    // Check if user is superuser or staff
    const isSuperUser = user.is_superuser || user.is_staff;

    // Check role from permissions (stored in user object after login)
    const permissions = (user as any).permissions || [];
    const role = (user as any).role || '';
    
    const isAdminRole = role.toLowerCase() === 'admin' || role.toLowerCase() === 'superadmin';
    
    // Admin can manage holidays
    const hasManagePermission = permissions.includes('holidays_manage') || isSuperUser || isAdminRole;

    // By default, all authenticated users can view and request holidays
    // You can restrict this by removing these default true values
    // and uncommenting the permission checks below
    return {
      canView: true,  // All authenticated users can view
      canRequest: true,  // All authenticated users can request
      canManage: hasManagePermission,  // Only admins can manage
      isAdmin: isSuperUser || isAdminRole || hasManagePermission,
    };
    
    /* Uncomment to use permission-based access:
    const hasViewPermission = permissions.includes('holidays_view') || isSuperUser || isAdminRole;
    const hasRequestPermission = permissions.includes('holidays_request') || isSuperUser || isAdminRole;
    
    return {
      canView: hasViewPermission,
      canRequest: hasRequestPermission,
      canManage: hasManagePermission,
      isAdmin: isSuperUser || isAdminRole || hasManagePermission,
    };
    */
  }, [user]);
};

export default useHolidayPermissions;
